// Frontend engagement suite
// Files included below (copy each into your src/ tree as indicated):
// 1) src/components/StarRating.jsx
// 2) src/components/FavoriteButton.jsx
// 3) src/components/CommentList.jsx
// 4) src/components/CommentForm.jsx
// 5) src/components/EngagementSummary.jsx
// 6) src/components/FavoritesSection.jsx (dashboards)
// 7) Integration examples for Artwork/Artist/Host pages & dashboards

// -----------------------------------------------------------------------------
// 1) src/components/StarRating.jsx
// -----------------------------------------------------------------------------
import React from "react";
import { api } from "../lib/api";
import EngagementAuthDialog from "./EngagementAuthDialog";
import { isEngagementAuthError } from "./engagementAuthNotice";

export default function StarRating({ target, objectId, value, onChange, readOnly = false }) {
  const [hover, setHover] = React.useState(null);
  const [current, setCurrent] = React.useState(value || 0);
  const [authDialogOpen, setAuthDialogOpen] = React.useState(false);

  React.useEffect(() => setCurrent(value || 0), [value]);

  const canEdit = !readOnly;

  async function setRating(stars) {
    if (!canEdit) return;
    try {
      const body = { target, object_id: objectId, stars };
      // Upsert: try create, and if 409-ish semantics aren't exposed, backend will unique-constrain & update via list->user match
      await api.postJson("/engagement/ratings/", body);
      onChange && onChange(stars);
    } catch (e) {
      console.warn("setRating failed", e);
      if (isEngagementAuthError(e)) {
        setAuthDialogOpen(true);
        return;
      }
      alert(e.message || "Unable to set rating");
    }
  }

  return (
    <>
      <div style={{ display: "inline-flex", gap: 4 }} aria-label="rating">
        {[1,2,3,4,5].map((n) => {
          const active = (hover ?? current) >= n;
          return (
            <svg key={n}
              onMouseEnter={() => canEdit && setHover(n)}
              onMouseLeave={() => canEdit && setHover(null)}
              onClick={() => setRating(n)}
              width="20" height="20" viewBox="0 0 20 20"
              style={{ cursor: canEdit ? "pointer" : "default" }}
              aria-hidden="true"
            >
              <path d="M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z"
                fill={active ? "#F59E0B" : "#E5E7EB"} />
            </svg>
          );
        })}
      </div>
      <EngagementAuthDialog open={authDialogOpen} onClose={() => setAuthDialogOpen(false)} />
    </>
  );
}


// -----------------------------------------------------------------------------
// 7) Integration examples
// -----------------------------------------------------------------------------
// A) Artwork Detail / Card (where you have an artwork object)
// import { EngagementSummary } from "../components/EngagementSummary";
// <EngagementSummary target="artwork" objectId={artwork.id} />
// <CommentList target="artwork" objectId={artwork.id} />
// {isAuthenticated && <CommentForm target="artwork" objectId={artwork.id} onPosted={() => {/* optionally refresh list */}} />}

// B) ArtistProfileCard.js
// import { EngagementSummary } from "../components/EngagementSummary";
// <EngagementSummary target="artist" objectId={artist.id} />
// <CommentList target="artist" objectId={artist.id} />
// {isAuthenticated && <CommentForm target="artist" objectId={artist.id} onPosted={() => {/* refresh */}} />}

// C) HostProfileCard.js
// <EngagementSummary target="host" objectId={host.id} />
// <CommentList target="host" objectId={host.id} />
// {isAuthenticated && <CommentForm target="host" objectId={host.id} />}

// D) Dashboards (Artist/Host/Buyer)
// import { FavoritesSection } from "../components/FavoritesSection";
// 
// <div style={{ display: "grid", gap: 16 }}>
//   <FavoritesSection type="artworks" />
//   <FavoritesSection type="artists" />
// </div>

// Notes:
// - Auth: These components rely on your api.js attaching OAuth Bearer and handling CSRF. Unauthenticated users can see lists/summary but will get 401 on write; we surface alerts accordingly.
// - Styling: Inline, dependency-free. Replace with your design system or Tailwind as needed.
// - Favorite DELETE: current FavoriteButton uses a convenience DELETE with query; if your backend only supports DELETE by id, expose that id in Favorite create response and store/use it here.
